import { useMutation, UseMutationOptions, useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { FirebaseError } from 'firebase/app';

import { axiosInstance } from '../../../axios';
import { fetchCollectionDocuments, fetchDocumentById } from '../../../queries/utils';
import { responseHandler } from '../../../utils';
import {
  CreateRoomServiceCategoryRequest,
  CreateRoomServiceProductRequest,
  CreateRoomServiceVariantRequest,
  DeleteRoomServiceCategoryRequest,
  DeleteRoomServiceConfigRequest,
  DeleteRoomServiceProductRequest,
  DeleteRoomServiceVariantRequest,
  RoomServiceCategoryData,
  RoomServiceConfigData,
  RoomServiceProductData,
  RoomServiceVariantData,
  UpdateRoomServiceCategoryRequest,
  UpdateRoomServiceProductRequest,
  UpdateRoomServiceVariantRequest,
} from './types';

// Get all Room Services for a hotel
// `queryKey`/`queryFn` dostarcza sam hook, więc caller ich nie podaje — stąd Omit
// (w react-query v5 `UseQueryOptions` wymaga `queryKey`, co blokowało przekazanie np. `refetchInterval`).
export const useGetAllRoomServices = (
  organizationId: string | null | undefined,
  options?: Omit<UseQueryOptions<(RoomServiceConfigData & { id: string })[], FirebaseError>, 'queryKey' | 'queryFn'>,
): UseQueryResult<(RoomServiceConfigData & { id: string })[], FirebaseError> => useQuery<(RoomServiceConfigData & { id: string })[], FirebaseError>({
  queryKey: ['room-service-configs', organizationId],
  queryFn: async () => {
    if (!organizationId) {
      return [];
    }
    const path = `hotels/${organizationId}/roomService`;
    const result = await fetchCollectionDocuments<RoomServiceConfigData>(path);
    // Ensure name is always a string (handle legacy data where name might be an object)
    return result.map((service) => ({
      ...service,
      name: typeof service.name === 'string' ? service.name : (service.name as any)?.pl || (service.name as any)?.en || '',
    }));
  },
  enabled: !!organizationId,
  retry: 0,
  ...options,
});

// Get single Room Service Config
export const useGetRoomServiceConfig = (
  organizationId: string | null | undefined,
  roomServiceId: string | null | undefined,
  options?: UseQueryOptions<(RoomServiceConfigData & { id: string }) | null, FirebaseError>,
): UseQueryResult<(RoomServiceConfigData & { id: string }) | null,
    FirebaseError> => useQuery<(RoomServiceConfigData & { id: string })
      | null, FirebaseError>({
        queryKey: ['room-service-config', organizationId, roomServiceId],
        queryFn: async () => {
          if (!organizationId || !roomServiceId) return null;

          try {
            const data = await fetchDocumentById<RoomServiceConfigData>(`hotels/${organizationId}/roomService`, roomServiceId);
            if (!data) return null;
            return { ...data, id: roomServiceId };
          } catch (error) {
            return null;
          }
        },
        enabled: !!organizationId && !!roomServiceId,
        retry: 0,
        ...options,
      });

// Get Categories
export const useGetRoomServiceCategories = (
  organizationId: string | null | undefined,
  roomServiceId: string | null | undefined,
  options?: Omit<UseQueryOptions<RoomServiceCategoryData[], FirebaseError>, 'queryKey' | 'queryFn'>,
): UseQueryResult<RoomServiceCategoryData[], FirebaseError> => useQuery<RoomServiceCategoryData[], FirebaseError>({
  queryKey: ['room-service-categories', organizationId, roomServiceId],
  queryFn: async () => {
    if (!organizationId || !roomServiceId) return [];
    return fetchCollectionDocuments<RoomServiceCategoryData>(`hotels/${organizationId}/roomService/${roomServiceId}/categories`);
  },
  enabled: !!organizationId && !!roomServiceId,
  retry: 0,
  ...options,
});

// Get Products
export const useGetRoomServiceProducts = (
  organizationId: string | null | undefined,
  roomServiceId: string | null | undefined,
  options?: Omit<UseQueryOptions<RoomServiceProductData[], FirebaseError>, 'queryKey' | 'queryFn'>,
): UseQueryResult<RoomServiceProductData[], FirebaseError> => useQuery<RoomServiceProductData[], FirebaseError>({
  queryKey: ['room-service-products', organizationId, roomServiceId],
  queryFn: async () => {
    if (!organizationId || !roomServiceId) return [];
    return fetchCollectionDocuments<RoomServiceProductData>(`hotels/${organizationId}/roomService/${roomServiceId}/products`);
  },
  enabled: !!organizationId && !!roomServiceId,
  retry: 0,
  ...options,
});

// Get Variants (współdzielone zestawy opcji outletu)
export const useGetRoomServiceVariants = (
  organizationId: string | null | undefined,
  roomServiceId: string | null | undefined,
  options?: Omit<UseQueryOptions<RoomServiceVariantData[], FirebaseError>, 'queryKey' | 'queryFn'>,
): UseQueryResult<RoomServiceVariantData[], FirebaseError> => useQuery<RoomServiceVariantData[], FirebaseError>({
  queryKey: ['room-service-variants', organizationId, roomServiceId],
  queryFn: async () => {
    if (!organizationId || !roomServiceId) return [];
    return fetchCollectionDocuments<RoomServiceVariantData>(`hotels/${organizationId}/roomService/${roomServiceId}/variants`);
  },
  enabled: !!organizationId && !!roomServiceId,
  retry: 0,
  ...options,
});

// ── Mutacje przepięte na backend v3 (codebase outlets). Cały CRUD przez admin SDK
//    (koniec client-side Firestore, §2.5). Metody HTTP wg semantyki: POST/PUT/DELETE. ──

// Category Mutations
export const useCreateCategory = (
  options?: UseMutationOptions<any, AxiosError, CreateRoomServiceCategoryRequest>,
) => useMutation({
  mutationFn: (request) => axiosInstance.post('/v3-createRoomServiceCategory', request).then(responseHandler),
  ...options,
});

export const useUpdateCategory = (
  options?: UseMutationOptions<any, AxiosError, UpdateRoomServiceCategoryRequest>,
) => useMutation({
  mutationFn: (request) => axiosInstance.put('/v3-updateRoomServiceCategory', request).then(responseHandler),
  ...options,
});

// single delete deleguje do scalonego endpointu 1..N (categoryIds: [id]); kasuje też produkty tej kategorii.
export const useDeleteCategory = (
  options?: UseMutationOptions<any, AxiosError, DeleteRoomServiceCategoryRequest>,
) => useMutation({
  mutationFn: ({ hotelId, roomServiceId, categoryId }) => axiosInstance.delete('/v3-deleteRoomServiceCategory', {
    data: { hotelId, roomServiceId, categoryIds: [categoryId] },
  }).then(responseHandler),
  ...options,
});

// Product Mutations
export const useCreateProduct = (
  options?: UseMutationOptions<any, AxiosError, CreateRoomServiceProductRequest>,
) => useMutation({
  mutationFn: (request) => axiosInstance.post('/v3-createRoomServiceProduct', request).then(responseHandler),
  ...options,
});

export const useUpdateProduct = (
  options?: UseMutationOptions<any, AxiosError, UpdateRoomServiceProductRequest>,
) => useMutation({
  mutationFn: (request) => axiosInstance.put('/v3-updateRoomServiceProduct', request).then(responseHandler),
  ...options,
});

// single delete deleguje do scalonego endpointu 1..N (productIds: [id]).
export const useDeleteProduct = (
  options?: UseMutationOptions<any, AxiosError, DeleteRoomServiceProductRequest>,
) => useMutation({
  mutationFn: ({ hotelId, roomServiceId, productId }) => axiosInstance.delete('/v3-deleteRoomServiceProduct', {
    data: { hotelId, roomServiceId, productIds: [productId] },
  }).then(responseHandler),
  ...options,
});

// Variant Mutations
export const useCreateVariant = (
  options?: UseMutationOptions<any, AxiosError, CreateRoomServiceVariantRequest>,
) => useMutation({
  mutationFn: (request) => axiosInstance.post('/v3-createRoomServiceVariant', request).then(responseHandler),
  ...options,
});

export const useUpdateVariant = (
  options?: UseMutationOptions<any, AxiosError, UpdateRoomServiceVariantRequest>,
) => useMutation({
  mutationFn: (request) => axiosInstance.put('/v3-updateRoomServiceVariant', request).then(responseHandler),
  ...options,
});

// single delete deleguje do scalonego endpointu 1..N (variantIds: [id]); produkty zostają,
// backend odpina od nich tylko usuwany wariant.
export const useDeleteVariant = (
  options?: UseMutationOptions<any, AxiosError, DeleteRoomServiceVariantRequest>,
) => useMutation({
  mutationFn: ({ hotelId, roomServiceId, variantIds }) => axiosInstance.delete('/v3-deleteRoomServiceVariant', {
    data: { hotelId, roomServiceId, variantIds },
  }).then(responseHandler),
  ...options,
});

// Room Service Config Delete — scalony endpoint 1..N (ids: [roomServiceId]); kaskada subkolekcji na backendzie.
export const useDeleteRoomServiceConfig = (
  options?: UseMutationOptions<any, AxiosError, DeleteRoomServiceConfigRequest>,
) => useMutation({
  mutationFn: ({ hotelId, roomServiceId }) => axiosInstance.delete('/v3-deleteRoomService', {
    data: { hotelId, ids: [roomServiceId] },
  }).then(responseHandler),
  ...options,
});
